// One of an issue's three short parts: shown as Markdown, edited as its
// source, and flagged when it is longer than its budget, never refused.
import { useRef, useState, type KeyboardEvent } from 'react'
import type { Issue, Part } from '../api/types'
import { Section } from '../components/layout'
import { Time } from '../components/Time'
import { useUnsavedChanges } from '../components/unsaved'
import { useWorkspaceDraft } from '../workspace/drafts'
import { Markdown } from '../markdown/Markdown'
import { useStore } from '../store/context'
import { PART_BUDGETS, PART_NAMES, partLength, partText } from '../vocab'

const EMPTY: Record<Part, string> = { description: 'No description yet.', plan: 'No plan yet.', outcome: 'No outcome yet.' }

/** The text being edited, and the text it started from. */
interface Edit {
  base: string
  draft: string
}

export function IssuePart({ issue, part, readOnly = false, initiallyEditing = false }: { issue: Issue; part: Part; readOnly?: boolean; initiallyEditing?: boolean }) {
  const store = useStore()
  const text = partText(issue, part)
  const [edit, setEdit] = useWorkspaceDraft<Edit | null>(
    `issue.${issue.id}.${part}`, initiallyEditing && !readOnly ? { base: text, draft: text } : null,
    (value): value is Edit | null => value === null || (
      typeof value === 'object' && value !== null
      && 'base' in value && typeof value.base === 'string'
      && 'draft' in value && typeof value.draft === 'string'
    ),
    value => value !== null && value.draft !== value.base,
  )
  useUnsavedChanges(!readOnly && edit !== null && edit.draft !== edit.base)
  const [saving, setSaving] = useState(false)
  const openAtStart = useRef(initiallyEditing)
  const name = PART_NAMES[part]

  const save = async () => {
    if (!edit || saving) return
    if (edit.draft === edit.base) {
      setEdit(null)
      return
    }
    setSaving(true)
    // From the text the edit started from, which the server merges with the
    // changes made since. A refused save keeps the editor open with the
    // draft; the store has said why.
    const saved = await store.savePart(issue.id, part, edit.draft, edit.base)
    setSaving(false)
    if (saved) setEdit(null)
  }

  const cancel = () => {
    if (!edit || saving) return
    if (edit.draft !== edit.base && !window.confirm('Discard your unsaved changes?')) return
    setEdit(null)
  }

  return (
    <Section
      title={name}
      actions={
        <span className="section-actions">
          {part === 'plan' && (!edit || readOnly) && issue.plan.approval && (
            <span className="tag tag-ok">
              Approved <Time iso={issue.plan.approval.at} />
            </span>
          )}
          <BudgetFlag part={part} text={readOnly ? text : edit?.draft ?? text} />
        </span>
      }
    >
      {edit && !readOnly ? (
        <PartEditor name={name} edit={edit} saving={saving} focus={!openAtStart.current} onChange={(draft) => setEdit({ ...edit, draft })} onSave={() => void save()} onCancel={cancel} />
      ) : (
        <div className="markdown-display">
          {!readOnly && <div className="markdown-display-actions">
            <button type="button" className="button button-quiet button-small" onClick={() => { openAtStart.current = false; setEdit({ base: text, draft: text }) }}>
              Edit {name.toLowerCase()}
            </button>
          </div>}
          {text.trim() ? <Markdown source={text} /> : <p className="muted">{EMPTY[part]}</p>}
        </div>
      )}
    </Section>
  )
}

function PartEditor({
  name,
  edit,
  saving,
  focus,
  onChange,
  onSave,
  onCancel,
}: {
  name: string
  edit: Edit
  saving: boolean
  /** Opened by the person, it takes the focus; open from the start, it leaves it to the title. */
  focus: boolean
  onChange: (draft: string) => void
  onSave: () => void
  onCancel: () => void
}) {
  const keys = (event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
      event.preventDefault()
      onSave()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      onCancel()
    }
  }
  return (
    <div className="markdown-editing">
      <textarea className="textarea" aria-label={name} value={edit.draft} onChange={(e) => onChange(e.target.value)} onKeyDown={keys} autoFocus={focus} />
      <div className="form-actions">
        <span className="muted form-hint form-hint-keys">⌘S saves · Esc cancels</span>
        <button type="button" className="button" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="button button-primary" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}

const NUMBER = new Intl.NumberFormat('en-GB')

/** Said beside a part that is longer than its budget. */
export function BudgetFlag({ part, text }: { part: Part; text: string }) {
  const length = partLength(text)
  const budget = PART_BUDGETS[part]
  if (length <= budget) return null
  return (
    <span className="tag tag-warn">
      Over budget: {NUMBER.format(length)} of {NUMBER.format(budget)} characters
    </span>
  )
}
